import JsonSchema, { JsonSchemaObject } from '@evidentid/json-schema/interfaces/JsonSchema';
import { isArray, isObject } from '@evidentid/json-schema/schemaChecks';
import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';

/** Whether a custom property of `propertyType` can supply a value for a criterion declaring `filterType`.
 *
 * Exact match, widened one way: every integer is a valid number, so an Integer custom property can
 * satisfy a criterion declaring `number` (PRODUCT-18727 -- "a client can set criteria of type IntValue
 * to custom properties of type Integer or Number"). Deliberately not symmetric: a Number property could
 * supply 1.5 where a criterion declaring `integer` requires a whole number, so those stay unoffered.
 */
function isTypeCompatible(propertyType: unknown, filterType: unknown): boolean {
    return propertyType === filterType || (propertyType === 'integer' && filterType === 'number');
}

function isEqualWhenCustomPropertyObject(customPropertySchema: JsonSchemaObject, schemaFilterBy: JsonSchema) {
    const typeEqual = isObject(schemaFilterBy) && isTypeCompatible(customPropertySchema.type, schemaFilterBy.type);
    const displayFormatEqual = (customPropertySchema as any).displayFormat ?
        (schemaFilterBy as any).displayFormat === (customPropertySchema as any).displayFormat
        : true;
    return typeEqual && displayFormatEqual;
}

export function filterCustomPropertiesBySchema(
    customProperties: CustomProperty[],
    schemaFilterBy?: JsonSchema | null,
): CustomProperty[] {
    if (!schemaFilterBy) {
        return customProperties;
    }

    return customProperties.filter(({ schema: customPropertySchema }) => {
        if (isObject(customPropertySchema)) {
            return isEqualWhenCustomPropertyObject(customPropertySchema, schemaFilterBy);
        } else if (isArray(customPropertySchema)) {
            const typeEqual = isArray(schemaFilterBy) && customPropertySchema.type === schemaFilterBy.type;
            if (!typeEqual) {
                return false;
            }
            const customPropertyItemsSchema = customPropertySchema.items as any;
            const schemaItemsFilterBy = schemaFilterBy.items as any;
            if (isObject(customPropertyItemsSchema)) {
                return isEqualWhenCustomPropertyObject(customPropertyItemsSchema, schemaItemsFilterBy);
            }
            return isTypeCompatible(customPropertyItemsSchema.type, schemaItemsFilterBy.type);
        }

        return isTypeCompatible((customPropertySchema as any).type, (schemaFilterBy as any).type);
    });
}
